<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Avatar, Badge, BrandMark, Divider, DemoBanner } from '@pinchcrew/ui'
import { usingMockApi } from '@pinchcrew/core'
import { useAuthStore } from './stores/auth'
import { useMoneyStore } from './stores/money'
import { useVettingStore } from './stores/vetting'
import { t } from './i18n'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const money = useMoneyStore()
const vetting = useVettingStore()

const showChrome = computed(() => auth.isAuthenticated && !route.meta.public)
const currentTab = computed(() => (route.meta.tab as string) ?? '')
const menuOpen = ref(false)

const NAV = computed(() => [
  { tab: 'vetting', to: '/vetting', label: t('nav.vetting'), badge: vetting.queue.length || undefined },
  { tab: 'disputes', to: '/disputes', label: t('nav.disputes'), badge: money.openDisputes.length || undefined },
  ...(auth.can.money ? [{ tab: 'payouts', to: '/payouts', label: t('nav.payouts') }] : []),
  { tab: 'orgs', to: '/orgs', label: t('nav.orgs') },
  { tab: 'rates', to: '/rates', label: t('nav.rates') },
  { tab: 'courses', to: '/courses', label: t('nav.courses') },
  { tab: 'metrics', to: '/metrics', label: t('nav.metrics') },
  { tab: 'audit', to: '/audit', label: t('nav.audit') },
])

async function signOut() {
  await auth.logout()
  router.replace('/login')
}
</script>

<template>
  <div class="shell" :class="{ 'shell--chrome': showChrome, 'shell--demo': usingMockApi }">
    <DemoBanner v-if="usingMockApi" />
    <aside v-if="showChrome" class="side" :class="{ 'side--open': menuOpen }">
      <div class="side__brand">
        <BrandMark size="sm" name="Ops" />
      </div>

      <div v-if="auth.user" class="side__me">
        <Avatar :name="auth.user.name" size="sm" />
        <span class="side__me-body">
          <span class="side__me-name">{{ auth.user.name }}</span>
          <span class="side__me-role">{{ t(`roles.${auth.user.role}`) }}</span>
        </span>
      </div>

      <nav class="side__nav">
        <button
          v-for="item in NAV" :key="item.tab" type="button"
          class="side__link" :class="{ 'side__link--on': currentTab === item.tab }"
          @click="router.push(item.to); menuOpen = false"
        >
          <span>{{ item.label }}</span>
          <Badge v-if="item.badge" variant="brand" size="sm">{{ item.badge }}</Badge>
        </button>
      </nav>

      <div class="side__foot">
        <Divider spacing="sm" />
        <button type="button" class="side__link" @click="signOut">{{ t('signOut') }}</button>
      </div>
    </aside>

    <div v-if="showChrome && menuOpen" class="scrim" @click="menuOpen = false" />

    <main class="main">
      <button v-if="showChrome" type="button" class="burger" aria-label="Menu" @click="menuOpen = true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M4 7h16M4 12h16M4 17h16" stroke-linecap="round" />
        </svg>
      </button>
      <div class="main__inner">
        <RouterView />
      </div>
    </main>
  </div>
</template>

<style scoped>
.shell { min-height: 100vh; background: var(--k-color-bg); }
/* Clear the fixed demo strip; the sidebar is sticky so it needs the offset too. */
.shell--demo { padding-top: var(--k-layout-demo-banner-height); }
.shell--demo .side { height: calc(100vh - var(--k-layout-demo-banner-height)); }
.shell--chrome { display: flex; }

.side {
  width: var(--k-layout-sidebar-width); flex: none; position: sticky; top: 0; height: 100vh;
  border-right: 1px solid var(--k-color-divider); background: var(--k-color-bg);
  padding: var(--k-space-6) var(--k-space-4);
  display: flex; flex-direction: column; gap: var(--k-space-4);
}
.side__brand { display: flex; align-items: center; gap: var(--k-space-2); }

.side__me {
  display: flex; align-items: center; gap: var(--k-space-2);
  padding: var(--k-space-2); border-radius: var(--k-radius-md); background: var(--k-color-bg-alt);
}
.side__me-body { display: flex; flex-direction: column; min-width: 0; }
.side__me-name {
  font-size: var(--k-font-size-sm); font-weight: var(--k-font-weight-medium);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.side__me-role { font-size: var(--k-font-size-xs); color: var(--k-color-text-muted); }

.side__nav { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.side__link {
  display: flex; align-items: center; justify-content: space-between; gap: var(--k-space-2);
  width: 100%; padding: var(--k-space-2) var(--k-space-3); border-radius: var(--k-radius-md);
  font-size: var(--k-font-size-sm); color: var(--k-color-text-body);
  cursor: pointer; text-align: left;
  transition: background-color var(--k-duration-base) var(--k-easing-standard);
}
.side__link:hover { background: var(--k-color-bg-alt); }
.side__link--on {
  background: var(--k-color-bg-inverse); color: var(--k-color-text-inverse);
  font-weight: var(--k-font-weight-medium);
}
.side__foot { display: flex; flex-direction: column; gap: var(--k-space-2); }

.main { flex: 1; min-width: 0; }
.main__inner { max-width: 1128px; padding: var(--k-space-8) var(--k-space-8) var(--k-space-20); }

.burger { display: none; }
.scrim { display: none; }

@media (max-width: 1127.98px) {
  .side {
    position: fixed; inset: 0 auto 0 0; z-index: var(--k-z-drawer);
    transform: translateX(-100%);
    transition: transform var(--k-duration-base) var(--k-easing-standard);
    box-shadow: var(--k-shadow-lg);
  }
  .side--open { transform: translateX(0); }
  .scrim { display: block; position: fixed; inset: 0; background: var(--k-color-overlay); z-index: var(--k-z-overlay); }
  .burger {
    display: inline-flex; align-items: center; justify-content: center;
    width: 40px; height: 40px; margin: var(--k-space-4) 0 0 var(--k-space-4);
    border: 1px solid var(--k-color-border); border-radius: var(--k-radius-md);
    background: var(--k-color-bg); cursor: pointer;
  }
  .main__inner { padding: var(--k-space-4) var(--k-space-4) var(--k-space-16); }
}
</style>
